import classNames from "classnames";
import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { Button, Form } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { notify } from "utils/notify";

import {
  useOpenPasswordResetLinkMutation,
  useResetPasswordMutation,
} from "components/services/authorization/authService";
import { validatePassword } from "components/utilities/sedra3/helpers";
import messages from "components/utilities/sedra3/messages";

import paths from "routes/paths";

const forgotPasswordPaths = {
  simple: paths.simpleForgotPassword,
  split: paths.splitForgotPassword,
  card: paths.cardForgotPassword,
};

// The key the page receives for the link stays in this tab's sessionStorage
// until the reset ends, so a reload of the page goes on with it. It is never
// put in the address, so it stays out of the history, and other tabs do not
// see it.
const KEY_IN_TAB = "passwordResetKey";
const keyInTab = {
  read: () => sessionStorage.getItem(KEY_IN_TAB),
  keep: (key) => sessionStorage.setItem(KEY_IN_TAB, key),
  remove: () => sessionStorage.removeItem(KEY_IN_TAB),
};

const tokenOfLink = (hash) => new URLSearchParams(hash.slice(1)).get("token");

// The server's answer, when the page opens the link or sets the password, for
// a link or key that no longer works: used already, expired, or replaced by a
// newer link. The reset has ended. Any other refusal ("new password must
// differ", 400) leaves it running.
const LINK_GONE = 410;

const PasswordResetForm = ({ hasLabel, layout = "simple" }) => {
  // State
  const location = useLocation();
  // Opened from a link, the page waits for the key its token is spent for.
  // Opened without one (a reload), it goes on with the key its tab holds; with
  // no key either, there is no reset to go on with: the link was used already
  // or the reset has ended.
  const [token, setToken] = useState(() =>
    tokenOfLink(location.hash) ? "" : keyInTab.read() ?? ""
  );
  const [linkUsed, setLinkUsed] = useState(
    () => !tokenOfLink(location.hash) && !keyInTab.read()
  );
  const navigate = useNavigate();
  const [resetPassword] = useResetPasswordMutation();
  const [openLink] = useOpenPasswordResetLinkMutation();
  const openedLinkToken = useRef(null);

  // An ordinary journey (a double click, the phone and then the desktop, a form
  // left open past its 30 minutes): the user is told so and pointed to a new
  // link, and the key this tab held goes with the reset.
  const showLinkUsed = () => {
    keyInTab.remove();
    setLinkUsed(true);
  };

  // The link carries the token in the URL fragment (#token=...), which the
  // browser never sends to the server. The page removes it from the address
  // bar and spends it at once for a key of its own: the copy the browser keeps
  // in its history no longer works, and the reset is finished with the key.
  // The ref keeps a second run of this effect from spending the key as well.
  useEffect(() => {
    const linkToken = tokenOfLink(location.hash);
    if (!linkToken || openedLinkToken.current === linkToken) return;
    openedLinkToken.current = linkToken;
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true }
    );
    openLink({ token: linkToken })
      .unwrap()
      .then((response) => {
        keyInTab.keep(response.data.key);
        setToken(response.data.key);
        // A newer link pasted into a tab that says "already used": the reset
        // goes on with it.
        setLinkUsed(false);
      })
      .catch((error) => {
        if (error?.status === LINK_GONE) {
          showLinkUsed();
          return;
        }
        notify.apiError(error, "UI_PASSWORD_RESET_FAILED");
      });
  }, [location, navigate, openLink]);

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });

  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  // Handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    const passwordValidationError = validatePassword(formData.password);
    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setConfirmPasswordError(messages.PASSWORDS_DO_NOT_MATCH);
      return;
    }

    setPasswordError("");
    setConfirmPasswordError("");

    try {
      const data = await resetPassword({
        new_password: formData.password,
        confirm_password: formData.confirmPassword,
        token,
      }).unwrap();

      keyInTab.remove();
      toast.success(data.message, { theme: "colored" });
      navigate(paths.cardLogin);
    } catch (error) {
      if (error?.status === LINK_GONE) {
        showLinkUsed();
        return;
      }
      notify.apiError(error, "UI_PASSWORD_RESET_FAILED");
    }
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Real-time validation for password
    if (name === "password") {
      const error = validatePassword(value);
      setPasswordError(error || "");
    }

    if (name === "confirmPassword") {
      if (value !== formData.password) {
        setConfirmPasswordError(messages.PASSWORDS_DO_NOT_MATCH);
      } else {
        setConfirmPasswordError("");
      }
    }
  };

  if (linkUsed) {
    return (
      <div className={classNames("mt-3", { "text-left": hasLabel })}>
        <p>{messages.PASSWORD_RESET_LINK_USED}</p>
        <p className="mb-0">
          To set your password, request a new link with{" "}
          <Link to={forgotPasswordPaths[layout]}>Forgot Password?</Link>
        </p>
      </div>
    );
  }

  return (
    <Form
      className={classNames("mt-3", { "text-left": hasLabel })}
      onSubmit={handleSubmit}
    >
      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>New Password</Form.Label>}
        <Form.Control
          placeholder={!hasLabel ? "New Password" : ""}
          value={formData.password}
          name="password"
          onChange={handleFieldChange}
          type="password"
          isInvalid={!!passwordError}
        />
        <Form.Control.Feedback type="invalid">
          {passwordError}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>Confirm Password</Form.Label>}
        <Form.Control
          placeholder={!hasLabel ? "Confirm Password" : ""}
          value={formData.confirmPassword}
          name="confirmPassword"
          onChange={handleFieldChange}
          type="password"
          isInvalid={!!confirmPasswordError}
        />
        <Form.Control.Feedback type="invalid">
          {confirmPasswordError}
        </Form.Control.Feedback>
      </Form.Group>

      <Button
        type="submit"
        className="w-100"
        disabled={
          !formData.password || !formData.confirmPassword || !!passwordError
        }
      >
        Set password
      </Button>
    </Form>
  );
};

PasswordResetForm.propTypes = {
  hasLabel: PropTypes.bool,
  layout: PropTypes.oneOf(Object.keys(forgotPasswordPaths)),
};

export default PasswordResetForm;
